/**
 * Shows one of Dr Cleanstein's answers.
 *
 * Deliberately a small renderer, not a Markdown library: paragraphs, bullet and
 * numbered lists, small headings, **bold** and tables. Everything is built as
 * React text nodes, so nothing in an answer can become HTML, an image or a link.
 * That matters because answers can quote record text that members of the public
 * typed (lead names, messages), and an image link is the one way such text could
 * carry data out of the portal. Web addresses therefore show as plain text.
 */

import type { ReactNode } from 'react';
import { stripCardBlocks } from '../../lib/studio/cardSpec';

export interface AnswerTable {
  headers: string[];
  rows: string[][];
}

type Block =
  | { kind: 'p'; lines: string[] }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] }
  | { kind: 'h'; text: string }
  | { kind: 'table'; table: AnswerTable };

const TABLE_RULE = /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?$/;

function splitRow(line: string): string[] {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
}

export function parseAnswer(text: string): Block[] {
  // Seth's Creative Studio card (a ```card block) is drawn in the Studio's preview, not shown as code.
  const lines = stripCardBlocks(text).replace(/\r/g, '').split('\n');
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const trimmed = (lines[i] ?? '').trim();
    if (!trimmed) { i++; continue; }

    if (trimmed.startsWith('|') && TABLE_RULE.test((lines[i + 1] ?? '').trim())) {
      const headers = splitRow(trimmed);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && (lines[i] ?? '').trim().startsWith('|')) {
        rows.push(splitRow(lines[i] ?? ''));
        i++;
      }
      blocks.push({ kind: 'table', table: { headers, rows } });
      continue;
    }

    const heading = trimmed.match(/^#{1,6}\s+(.*)$/);
    if (heading) { blocks.push({ kind: 'h', text: heading[1] ?? '' }); i++; continue; }

    if (/^[-*•]\s+/.test(trimmed) || /^\d+[.)]\s+/.test(trimmed)) {
      const ordered = /^\d+[.)]\s+/.test(trimmed);
      const items: string[] = [];
      while (i < lines.length) {
        const t = (lines[i] ?? '').trim();
        const m = ordered ? t.match(/^\d+[.)]\s+(.*)$/) : t.match(/^[-*•]\s+(.*)$/);
        if (!m) break;
        items.push(m[1] ?? '');
        i++;
      }
      blocks.push(ordered ? { kind: 'ol', items } : { kind: 'ul', items });
      continue;
    }

    const para: string[] = [];
    while (i < lines.length) {
      const t = (lines[i] ?? '').trim();
      if (!t || t.startsWith('|') || /^#{1,6}\s/.test(t) || /^[-*•]\s+/.test(t) || /^\d+[.)]\s+/.test(t)) break;
      para.push(t);
      i++;
    }
    if (para.length === 0) { para.push(trimmed); i++; }
    blocks.push({ kind: 'p', lines: para });
  }
  return blocks;
}

export function answerTables(text: string): AnswerTable[] {
  return parseAnswer(text).flatMap((b) => (b.kind === 'table' ? [b.table] : []));
}

/** Strips the **bold** markers for plain-text uses such as export and copy. */
export function plainCell(text: string): string {
  return text.replace(/\*\*/g, '').replace(/`/g, '');
}

/** **bold** only. Everything else, including any web address, stays plain text. */
function inline(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, idx) =>
    part.startsWith('**') && part.endsWith('**') && part.length > 4
      ? <strong key={idx}>{part.slice(2, -2)}</strong>
      : part.replace(/`/g, ''),
  );
}

interface AskAnswerProps {
  text: string;
  /** Shown under each table when given: saves that table as an Excel file. */
  onExportTable?: (table: AnswerTable) => void;
}

export default function AskAnswer({ text, onExportTable }: AskAnswerProps) {
  return (
    <div className="ask-answer">
      {parseAnswer(text).map((b, idx) => {
        if (b.kind === 'h') return <h4 key={idx}>{inline(b.text)}</h4>;
        if (b.kind === 'ul' || b.kind === 'ol') {
          const items = b.items.map((it, j) => <li key={j}>{inline(it)}</li>);
          return b.kind === 'ol' ? <ol key={idx}>{items}</ol> : <ul key={idx}>{items}</ul>;
        }
        if (b.kind === 'table') {
          return (
            <div key={idx} className="ask-table-block">
              <div className="ask-table-wrap">
                <table>
                  <thead>
                    <tr>{b.table.headers.map((h, j) => <th key={j}>{inline(h)}</th>)}</tr>
                  </thead>
                  <tbody>
                    {b.table.rows.map((r, j) => (
                      <tr key={j}>{r.map((c, k) => <td key={k}>{inline(c)}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {onExportTable && (
                <button type="button" className="ask-ghost-btn" onClick={() => onExportTable(b.table)}>
                  Download as Excel
                </button>
              )}
            </div>
          );
        }
        return (
          <p key={idx}>
            {b.lines.map((l, j) => (
              <span key={j}>
                {j > 0 && <br />}
                {inline(l)}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
}
